import type { DefinedAppAction } from 'actions/types/ActionTypes'
import type { ThunkAction } from 'redux-thunk'
import config from 'constants/config'
import { LabFeatureFlags } from 'constants/lab'
import waitForLoyaltyAccountFetch from 'loyalty/lib/waitForLoyaltyAccountFetch'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getLoyaltyAutoEnrolmentRegion from 'loyalty/selectors/getLoyaltyAutoEnrolmentRegion'
import enrolLoyaltyAccount from './enrolLoyaltyAccount'

/**
 * Enrols a signed-in customer who isn't a loyalty member, without asking them (LOYAL-2968).
 *
 * Waits for the account read userMiddleware starts at sign-in rather than reading again, and only
 * enrols when that read found no account. So an existing member is never re-enrolled and a closed
 * account (an error on the read) is left alone. Failures are silent so sign-in isn't affected.
 *
 * Gated by the `loyalty:autoEnrolExistingMembers` LE Lab flag. It reads the decision made for the
 * visitor before they signed in, as the post-login re-decision can land after this runs.
 *
 * Backs off while a customer enrolment is pending (setLoyaltyCustomerEnrolmentPending): the
 * customer clicked an enrol CTA, which enrols them once they're signed in, so they're recorded as
 * CUSTOMER rather than AUTO_ENROLLED.
 */
function autoEnrolLoyaltyAccount(): ThunkAction<Promise<void>, App.State, undefined, DefinedAppAction> {
  return async(dispatch, getState) => {
    const state = getState()

    if (
      !state.system.features.labFlags[LabFeatureFlags.loyaltyAutoEnrolExistingMembers]?.enabled ||
      config.agentHub.isEnabled ||
      state.system.headlessMode ||
      state.loyalty.isCustomerEnrolmentPending
    ) return

    await waitForLoyaltyAccountFetch(getState)
    const latestState = getState()
    const { account } = latestState.loyalty
    // `initial` means the read never ran, e.g. loyalty is off for this region
    if (account.initial || account.fetching || account.error || getHasLoyaltyAccount(latestState)) return

    const region = getLoyaltyAutoEnrolmentRegion(latestState)
    if (!region) return

    dispatch(enrolLoyaltyAccount({
      region,
      enrolmentSource: 'AUTO_ENROLLED',
    }))
  }
}

export default autoEnrolLoyaltyAccount
